
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Playlist, Song } from '../types';

const PlaylistsPage: React.FC = () => {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  
  const [createData, setCreateData] = useState({
    name: '',
    description: '',
    visibility: 'public' as const,
    is_collaborative: false
  });
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);
  
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [playlistSongs, setPlaylistSongs] = useState<Song[]>([]);
  const [allSongs, setAllSongs] = useState<Song[]>([]);
  const [searchSongTerm, setSearchSongTerm] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('playlists').select('*').order('created_at', { ascending: false });
      if (error) {
        if (error.message.includes('song_count')) {
          console.warn("Schema cache out of sync. Please use the Fix/Settings page to refresh PostgREST.");
        }
        throw error;
      }
      if (data) setPlaylists(data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let coverUrl = '';
      if (coverFile) {
        const path = `playlist-covers/${Date.now()}_${coverFile.name}`;
        const { error: upErr } = await supabase.storage.from('music').upload(path, coverFile);
        if (upErr) throw upErr;
        coverUrl = supabase.storage.from('music').getPublicUrl(path).data.publicUrl;
      }

      const { error } = await supabase.from('playlists').insert({ 
        name: createData.name,
        description: createData.description,
        visibility: createData.visibility,
        is_collaborative: createData.is_collaborative,
        cover_url: coverUrl || null,
        created_by: user.id
      });
      
      if (error) throw error;
      
      setShowCreateModal(false);
      setCreateData({ name: '', description: '', visibility: 'public', is_collaborative: false });
      setCoverFile(null);
      fetchPlaylists();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCreating(false);
    }
  };

  const fetchManageData = async (playlist: Playlist) => {
    setSelectedPlaylist(playlist);
    setShowManageModal(true);
    
    const { data: psData } = await supabase
      .from('playlist_songs')
      .select('song_id, position')
      .eq('playlist_id', playlist.id)
      .order('position', { ascending: true });
    
    const linkedIds = psData?.map(p => p.song_id) || [];
    
    const { data: sData } = await supabase.from('songs').select('*').order('title');
    if (sData) {
      setAllSongs(sData);
      const orderedSongs = linkedIds.map(id => sData.find(s => s.id === id)).filter(Boolean) as Song[];
      setPlaylistSongs(orderedSongs);
    }
  };

  const toggleSongInPlaylist = async (song: Song) => {
    if (!selectedPlaylist) return;
    setLinking(true);
    const { data: { user } } = await supabase.auth.getUser();
    const isLinked = playlistSongs.some(ps => ps.id === song.id);

    try {
      if (isLinked) {
        await supabase.from('playlist_songs').delete().eq('playlist_id', selectedPlaylist.id).eq('song_id', song.id);
        setPlaylistSongs(prev => prev.filter(p => p.id !== song.id));
      } else {
        const nextPosition = playlistSongs.length + 1;
        await supabase.from('playlist_songs').insert({ 
          playlist_id: selectedPlaylist.id, 
          song_id: song.id, 
          position: nextPosition,
          added_by: user?.id 
        });
        setPlaylistSongs(prev => [...prev, song]);
      }
      fetchPlaylists();
    } catch (e) {
      alert("Synchronization error: This track might already be present or its position index is invalid.");
    } finally {
      setLinking(false);
    }
  };

  const deletePlaylist = async (id: string) => {
    if (!confirm('Are you sure you want to delete this collection?')) return;
    await supabase.from('playlists').delete().eq('id', id);
    fetchPlaylists();
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
        <div>
          <h2 className="text-4xl font-black text-white tracking-tighter uppercase">Collections</h2>
          <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px] mt-2">Playlist Management Core</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="bg-accent-purple hover:bg-accent-purple/90 text-white px-10 py-4.5 rounded-[2rem] font-black text-xs uppercase tracking-widest flex items-center space-x-3 shadow-2xl transition-all border border-white/5 active:scale-95"
        >
          <i className="fas fa-layer-group text-lg"></i>
          <span>Create New List</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {loading ? [1,2,3].map(i => <div key={i} className="glass h-[420px] rounded-[3.5rem] animate-pulse" />) : playlists.map(playlist => (
          <div key={playlist.id} className="glass p-10 rounded-[3.5rem] border-white/5 shadow-2xl group hover:border-accent-purple/40 transition-all duration-500 relative overflow-hidden flex flex-col h-[480px]">
            <div className="absolute top-0 right-0 w-48 h-48 bg-accent-purple/5 rounded-full blur-[80px] -mr-24 -mt-24 group-hover:bg-accent-purple/10 transition-colors"></div>
            
            <div className="flex items-center space-x-6 mb-10">
              <div className="w-28 h-28 rounded-[2rem] overflow-hidden shadow-2xl border border-white/10 group-hover:scale-105 transition-transform">
                <img src={playlist.cover_url || `https://picsum.photos/seed/${playlist.id}/300/300`} className="w-full h-full object-cover" alt="" />
              </div>
              <div className="flex-1">
                <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${
                  playlist.visibility === 'public' ? 'bg-cyan-500/10 text-cyan-500' : 'bg-slate-500/10 text-slate-500'
                }`}>
                  {playlist.visibility}
                </span>
                <h4 className="text-2xl font-black text-white tracking-tighter line-clamp-2 mt-2 leading-tight">{playlist.name}</h4>
              </div>
            </div>

            <p className="text-slate-400 text-sm font-medium leading-relaxed line-clamp-3 mb-10 flex-1">{playlist.description || 'Custom curated music selection for portal users.'}</p>

            <div className="flex items-center justify-between mb-8">
               <div className="flex space-x-6">
                  <div className="text-center">
                    <p className="text-white font-black text-sm">{playlist.song_count || 0}</p>
                    <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest">Tracks</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white font-black text-sm">{playlist.play_count || 0}</p>
                    <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest">Streams</p>
                  </div>
               </div>
            </div>

            <div className="pt-8 border-t border-white/5 flex items-center justify-between">
                <button 
                  onClick={() => fetchManageData(playlist)} 
                  className="bg-white/5 text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-purple transition-all border border-white/5"
                >
                  Edit Tracks
                </button>
                <button onClick={() => deletePlaylist(playlist.id)} className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-slate-500 hover:text-red-500 transition-all hover:bg-red-500/10">
                  <i className="fas fa-trash-alt"></i>
                </button>
            </div>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-[#020617]/95 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="glass w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500">
            <div className="p-10 border-b border-white/5 flex justify-between items-center bg-white/[0.01]">
              <h3 className="text-2xl font-black text-white uppercase tracking-tighter">New Collection</h3>
              <button onClick={() => setShowCreateModal(false)} className="w-12 h-12 rounded-2xl bg-white/5 text-slate-400 hover:text-white transition-all"><i className="fas fa-times text-xl"></i></button>
            </div>
            <form onSubmit={handleCreate} className="p-10 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Title</label>
                <input required type="text" value={createData.name} onChange={e => setCreateData({...createData, name: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-purple/40 font-bold" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Visibility</label>
                  <select value={createData.visibility} onChange={e => setCreateData({...createData, visibility: e.target.value as any})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-purple/40 font-bold">
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                    <option value="unlisted">Unlisted</option>
                  </select>
                </div>
                <div className="flex flex-col justify-center pt-5">
                   <label className="flex items-center space-x-3 cursor-pointer group">
                      <input type="checkbox" checked={createData.is_collaborative} onChange={e => setCreateData({...createData, is_collaborative: e.target.checked})} className="hidden" />
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${createData.is_collaborative ? 'bg-accent-purple border-accent-purple' : 'border-white/10 group-hover:border-accent-purple/50'}`}>
                        {createData.is_collaborative && <i className="fas fa-check text-white text-[10px]"></i>}
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Collaborative</span>
                   </label>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Description</label>
                <textarea rows={3} value={createData.description} onChange={e => setCreateData({...createData, description: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-purple/40 font-bold resize-none" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Cover Art</label>
                <input type="file" accept="image/*" onChange={e => setCoverFile(e.target.files?.[0] || null)} className="w-full bg-white/5 border border-dashed border-white/10 p-4 rounded-2xl text-[10px] text-slate-400 font-bold" />
              </div>
              <button disabled={creating} type="submit" className="w-full bg-accent-purple text-white py-5 rounded-[1.8rem] font-black text-xs uppercase tracking-widest shadow-xl transition-all disabled:opacity-50 active:scale-95">
                {creating ? 'Processing...' : 'Save Collection'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showManageModal && selectedPlaylist && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-[#020617]/95 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="glass w-full max-w-3xl h-[85vh] rounded-[3rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-500">
            <div className="p-10 border-b border-white/5 flex justify-between items-center bg-white/[0.01]">
              <div className="flex items-center space-x-8">
                <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-xl border border-white/10">
                  <img src={selectedPlaylist.cover_url || ''} className="w-full h-full object-cover" alt="" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white uppercase tracking-tighter leading-none">{selectedPlaylist.name}</h3>
                  <p className="text-accent-purple text-[10px] font-black uppercase tracking-[0.3em] mt-2">Track Orchestration</p>
                </div>
              </div>
              <button onClick={() => setShowManageModal(false)} className="w-12 h-12 rounded-2xl bg-white/5 text-slate-400 hover:text-white transition-all"><i className="fas fa-times text-xl"></i></button>
            </div>
            
            <div className="p-8 border-b border-white/5 bg-white/[0.01]">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Query library for tracks..." 
                  value={searchSongTerm}
                  onChange={e => setSearchSongTerm(e.target.value)}
                  className="w-full bg-[#020617] border border-white/5 text-white px-12 py-4 rounded-[1.5rem] focus:ring-2 focus:ring-accent-blue/40 font-bold placeholder-slate-600"
                />
                <i className="fas fa-search absolute left-5 top-1/2 -translate-y-1/2 text-slate-600"></i>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-10 space-y-4 custom-scrollbar">
              {allSongs.filter(s => 
                s.title.toLowerCase().includes(searchSongTerm.toLowerCase()) || 
                s.artist.toLowerCase().includes(searchSongTerm.toLowerCase())
              ).map(song => {
                const isLinked = playlistSongs.some(ps => ps.id === song.id);
                return (
                  <div key={song.id} className="flex items-center justify-between p-4 rounded-[2rem] bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-all group/item">
                    <div className="flex items-center space-x-6">
                      <img src={song.cover_url || ''} className="w-14 h-14 rounded-2xl object-cover shadow-lg border border-white/10" alt="" />
                      <div>
                        <p className="font-extrabold text-white text-[15px] tracking-tight">{song.title}</p>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-0.5">{song.artist}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => toggleSongInPlaylist(song)}
                      disabled={linking}
                      className={`px-8 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                        isLinked 
                        ? 'bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white' 
                        : 'bg-accent-blue/10 text-accent-blue border border-accent-blue/20 hover:bg-accent-blue hover:text-white'
                      }`}
                    >
                      {isLinked ? 'Remove' : 'Add'}
                    </button>
                  </div>
                );
              })}
            </div>
            
            <div className="p-8 bg-white/[0.01] border-t border-white/5 flex items-center justify-between text-[10px] font-black text-slate-600 uppercase tracking-widest">
               <span>Total Tracks: {playlistSongs.length}</span>
               <span>Engine: Core-V2</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistsPage;
